import { Typography } from '@mui/material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import { FC, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import StudentAdmissionView from './studentAdmission/studentAdmission.view';
import { useStudentAdmissionStepsHook } from './studentAdmission/useStudentAdmissionStepsHook';
import { TStudentDetails } from '../../../../../apis/students/students.interfaces';
import { useStudentsActions } from '../../contexts/students.context';
import { mockStudentWithFeeDetails } from '../../../../../__mocks__/students/studentAdmissionFeeMock';
import { useTranslation } from '../../../../../hooks/useTranslationHook';
import { useGradeAndFeeStructureHook } from '../../../institute/hooks/useGradeAndFeeStructureHook';
import { useStudentsService } from '../../../../../apis/students/useStudentsService';
import { localStorageGet } from 'src/utils/localStorage';
import { ILoggedInUser } from 'src/apis/auth/auth.interfaces';
import { getStudentListKey } from '../../hooks/useStudentListHook';

type StudentAddDialogProps = {
  isOpen: boolean;
  onClose: (isRefreshStudentList: boolean) => void;
  errorMessage: string;
  studentDetails: TStudentDetails | null;
  isLoading: boolean;
};

const StudentManageDialog: FC<StudentAddDialogProps> = ({
  isOpen,
  onClose,
  errorMessage,
  studentDetails,
}) => {
  const { translateText } = useTranslation();
  const queryClient = useQueryClient();
  const { setGradeAndFeeStructure } = useStudentsActions();
  const { data: gradeAndFeeStructure } = useGradeAndFeeStructureHook();
  const { updateStudent } = useStudentsService();
  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = userFromStorage?.id || '';

  const { steps: admissionSteps, admissionStudentId } = useStudentAdmissionStepsHook(
    studentDetails ??
    (process.env.REACT_APP_USE_MOCK_ADMISSION_FEE === 'true'
      ? (mockStudentWithFeeDetails as TStudentDetails)
      : null)
  );
  const [isComplete, setIsComplete] = useState(false);

  const handleAdmissionWorkflowComplete = useCallback(async () => {
    const id = admissionStudentId;
    if (!id || !loggedInUserId) {
      setIsComplete(true);
      queryClient.invalidateQueries({ queryKey: [getStudentListKey] });
      return;
    }
    try {
      await updateStudent(id, { updatedBy: loggedInUserId });
      setIsComplete(true);
      queryClient.invalidateQueries({ queryKey: [getStudentListKey] });
    } catch {
      // Keep stepper visible; surface errors via app error handling if needed
    }
  }, [admissionStudentId, loggedInUserId, updateStudent]);

  const onCloseHandler = (isRefreshStudentList: boolean = false) => {
    setIsComplete(false);
    onClose(isRefreshStudentList);
  };

  useEffect(() => {
    if (isOpen && gradeAndFeeStructure) {
      setGradeAndFeeStructure(gradeAndFeeStructure);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, gradeAndFeeStructure]);

  useEffect(() => {
    return () => {
      setIsComplete(false);
    };
  }, []);

  return (
    <StructuredModal
      isOpen={isOpen}
      onClose={() => onCloseHandler(false)}
      title={translateText('students.addStudent')}
      drawerWidth="80vw"
      footerPositiveAction={isComplete ? () => onCloseHandler(true) : undefined}
      footerPositiveActionText={
        isComplete ? translateText('students.closeAndGoBackToList') : undefined
      }
      footerMessage={
        errorMessage ? (
          <Typography
            color="error"
            variant="body2"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            {errorMessage}
          </Typography>
        ) : null
      }
    >
      <StudentAdmissionView
        admissionSteps={admissionSteps}
        onCompleteHandler={handleAdmissionWorkflowComplete}
        editMode={!!studentDetails}
      />
    </StructuredModal>
  );
};

export default StudentManageDialog;
